/**
 * D365 workflow export files (Workflows list → Export): a serialized WorkflowExportDefinition,
 * written by D365 as UTF-16LE. Retargeting changes only the company the configuration belongs
 * to (`<companyId>` and `<company>`); every other byte stays as exported, in the same encoding,
 * so the file imports through Workflows → Import exactly like the original.
 */

export interface DecodedWorkflowExport {
  xml: string;
  encoding: 'utf-16le' | 'utf-8';
  /** The source started with a byte-order mark; the retargeted file keeps it. */
  bom: boolean;
}

export interface WorkflowExportSummary {
  name: string;
  company: string;
  category: string;
  template: string;
  documentTable: string;
  sequenceNumber: string;
  version: string;
  owner: string | null;
  /** Named users in step assignments (must exist and have access to the new company) */
  users: string[];
  /** User group participants (GL_Apv_Reg, Approvers, …) */
  userGroups: string[];
  /** Security role participants (-SYSADMIN-, …) */
  roles: string[];
  /** Hierarchy providers referenced (only used when a step's assignment type is Hierarchy) */
  hierarchyProviders: string[];
  /** Work calendars used for due dates; calendars are company-specific in D365 */
  calendars: string[];
  /** Amount conditions on gateways or steps, e.g. GreaterThan 25000 USD */
  conditions: Array<{ operator: string; amount: number; currency: string }>;
  /** Step assignment types in the file: Participant, User, Hierarchy, Queue … */
  assignmentTypes: string[];
}

export function decodeWorkflowExport(bytes: Uint8Array): DecodedWorkflowExport {
  if (bytes[0] === 0xff && bytes[1] === 0xfe)
    return { xml: new TextDecoder('utf-16le').decode(bytes.subarray(2)), encoding: 'utf-16le', bom: true };
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)
    return { xml: new TextDecoder('utf-8').decode(bytes.subarray(3)), encoding: 'utf-8', bom: true };
  // D365 writes UTF-16LE without a BOM: "<" then a zero byte.
  if (bytes[0] === 0x3c && bytes[1] === 0x00)
    return { xml: new TextDecoder('utf-16le').decode(bytes), encoding: 'utf-16le', bom: false };
  return { xml: new TextDecoder('utf-8').decode(bytes), encoding: 'utf-8', bom: false };
}

export function encodeWorkflowExport(xml: string, like: Omit<DecodedWorkflowExport, 'xml'>): Uint8Array {
  if (like.encoding === 'utf-8') {
    const body = new TextEncoder().encode(xml);
    return like.bom ? new Uint8Array([0xef, 0xbb, 0xbf, ...body]) : body;
  }
  const offset = like.bom ? 2 : 0;
  const out = new Uint8Array(offset + xml.length * 2);
  if (like.bom) out.set([0xff, 0xfe]);
  for (let i = 0; i < xml.length; i++) {
    const code = xml.charCodeAt(i);
    out[offset + i * 2] = code & 0xff;
    out[offset + i * 2 + 1] = code >> 8;
  }
  return out;
}

const unescapeXml = (s: string): string =>
  s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

/** Text of every element with this local name (any or no namespace prefix). */
function texts(xml: string, local: string): string[] {
  const re = new RegExp(`<(?:\\w+:)?${local}(?:\\s[^>]*)?>([^<]*)</(?:\\w+:)?${local}>`, 'g');
  return [...xml.matchAll(re)].map((m) => unescapeXml(m[1] ?? ''));
}

const first = (xml: string, local: string): string => texts(xml, local)[0] ?? '';
const unique = (values: string[]): string[] => [...new Set(values.filter(Boolean))].sort();

/** Participant tokens of one provider (user groups, roles). */
function participants(xml: string, provider: string): string[] {
  const re = new RegExp(
    `<(?:\\w+:)?providerName[^>]*>${provider}</(?:\\w+:)?providerName><(?:\\w+:)?tokenName[^>]*>([^<]*)<`,
    'g',
  );
  return unique([...xml.matchAll(re)].map((m) => unescapeXml(m[1] ?? '')));
}

export function summarizeWorkflowExport(xml: string): WorkflowExportSummary {
  if (!xml.includes('WorkflowExportDefinition'))
    throw new Error('Not a D365 workflow export (no WorkflowExportDefinition)');
  const workflow = xml.slice(xml.indexOf('<workflow'));
  const major = first(xml, 'versionIdMajor');
  return {
    name: first(workflow, 'name'),
    company: first(xml, 'companyId'),
    category: first(xml, 'categoryName'),
    template: first(xml, 'templateName'),
    documentTable: first(xml, 'documentTableName'),
    sequenceNumber: first(xml, 'sequenceNumber'),
    version: major
      ? [
          major,
          first(xml, 'versionIdMinor'),
          first(xml, 'versionIdBuild'),
          first(xml, 'versionIdRevision'),
        ].join('.')
      : '',
    owner: first(xml, 'owner') || null,
    users: unique(texts(xml, 'user').flatMap((u) => u.split(';').map((x) => x.trim()))),
    userGroups: participants(xml, 'WorkflowUserGroupParticipantProvider'),
    roles: participants(xml, 'WorkflowRoleParticipantProvider'),
    hierarchyProviders: unique(texts(xml, 'providerName').filter((p) => /Hierarchy/.test(p))),
    calendars: unique(texts(xml, 'calendar')),
    conditions: [
      ...xml.matchAll(
        /<(?:\w+:)?numericOperator>(\w+)<\/(?:\w+:)?numericOperator><(?:\w+:)?numericValue>([\d.]+)<\/(?:\w+:)?numericValue>(?:<(?:\w+:)?currencyCode[^>]*>(\w*)<)?/g,
      ),
    ].map((m) => ({ operator: m[1] ?? '', amount: Number(m[2]), currency: m[3] ?? '' })),
    assignmentTypes: unique(texts(xml, 'assignmentType').filter((t) => t !== 'None')),
  };
}

/**
 * The same export for another company: only the `<companyId>` and `<company>` values change, each
 * in the case the export used (D365 writes `<company>` in lower case and, in newer exports,
 * `<companyId>` in upper case: USMF / usmf). Refuses a file that doesn't have exactly one of each,
 * rather than guessing.
 */
export function retargetWorkflowExport(xml: string, companyId: string, owner?: string | null): string {
  const company = companyId.trim().toLowerCase();
  if (!/^[a-z0-9]{1,4}$/.test(company))
    throw new Error(`"${companyId}" is not a D365 company ID (1–4 letters or digits)`);
  let out = xml;
  for (const tag of ['companyId', 'company']) {
    const re = new RegExp(`(<${tag}(?:\\s[^>]*)?>)([^<]*)(</${tag}>)`, 'g');
    const found = [...out.matchAll(re)].length;
    if (found !== 1) throw new Error(`Expected one <${tag}> in the export, found ${found}`);
    out = out.replace(re, (_m, open: string, old: string, close: string) => {
      const upper = /[A-Z]/.test(old) && old === old.toUpperCase();
      return `${open}${upper ? company.toUpperCase() : company}${close}`;
    });
  }
  // The owner must be a user in the target environment (the USMF export's SARA isn't in TRN).
  if (owner) {
    const re = /(<owner(?:\s[^>]*)?>)([^<]*)(<\/owner>)/g;
    const found = [...out.matchAll(re)].length;
    if (found !== 1) throw new Error(`Expected one <owner> in the export, found ${found}`);
    out = out.replace(
      re,
      (_m, open: string, _old: string, close: string) => `${open}${xmlText(owner)}${close}`,
    );
  }
  return out;
}

const xmlText = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** File name for the retargeted export: the original name with the company in front. */
export const retargetedFileName = (original: string, companyId: string): string =>
  `${companyId.toUpperCase()} - ${original.replace(/^.*[\\/]/, '')}`;
